import { StructuredData } from 'components/StructuredData';
import { structuredProductList } from 'components/StructuredData/structuredDataHelpers';
import { useFlags } from 'gatsby-plugin-launchdarkly';
import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useQueryParam, NumberParam, BooleanParam } from 'use-query-params';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { Flags } from 'utils/constants/featureFlags';
import { TITLES, DESCRIPTIONS } from 'utils/constants/meta';
import { COVID_SLUGS } from 'utils/constants/products';
import { APP_ROOT } from 'utils/constants/urls';
import {
  filterUniqueByCategory,
  getProductsOrderedByPopularity,
} from 'utils/helpers';
import { getActivePromo } from 'utils/helpers/activePromoHelpers';
import useProductCategories from 'utils/hooks/useProductCategories';
import usePromos from 'utils/hooks/usePromos';
import { ProductInfoMap, ProductDataType } from 'utils/types';

import Layout from '../components/Layout';
import PageControlSection from '../components/PIP/PageControlSection';
import PageHeading from '../components/PIP/PageHeading';
import ProductDisplayGrid from '../components/PIP/ProductDisplayGrid';
import { PromoProps } from '../components/PIP/ProductDisplayGrid/PromoCard';
import { PrePurchaseModalContextProvider } from '../contexts/PrePurchaseModal';
import { SEO } from '../components/SEO';

type Props = {
  // TODO: CG-1474
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pageContext: any;
};
export const ProductInfoContext = createContext<ProductInfoMap>({});

const PIPTemplate = (props: Props): JSX.Element => {
  const { pageContext } = props;

  const { allTests, advertisedPromos } = pageContext;

  const { productCategoryMap } = useProductCategories();

  const { activePromos } = usePromos();

  const [activeCategory, setActiveCategory] = useQueryParam(
    'category',
    NumberParam,
  );

  const flags = useFlags();
  const flagToShowCovidCategory = flags[Flags.CovidPIPCategory];
  const [disableC19Param] = useQueryParam('disableC19', BooleanParam);

  const pcmKeys = Object.keys(productCategoryMap);

  const [productCategoryKeys, setProductCategoryKeys] =
    useState<Array<string>>(pcmKeys);

  const activePromo = useMemo(
    () => getActivePromo(advertisedPromos),
    [advertisedPromos],
  );

  // Array of all products - contains duplicate entries for products shared between categories
  const showableTests = useMemo(() => {
    const filteredTests: ProductDataType[] = allTests.filter(
      (p: ProductDataType) => {
        if (disableC19Param) {
          return !COVID_SLUGS.includes(p.slug);
        } else {
          return true;
        }
      },
    );

    return getProductsOrderedByPopularity(filteredTests);
  }, []);

  // Array of unique test objects
  const uniqueTests = useMemo<ProductDataType[]>(
    () =>
      showableTests.reduce((unique: ProductDataType[], o: ProductDataType) => {
        if (!unique.some((obj) => obj.productId === o.productId)) {
          unique.push(o);
        }
        return unique;
      }, []),
    [],
  );

  // Remove Covid from Categories if URL Param present
  useEffect(() => {
    if (flagToShowCovidCategory === false || disableC19Param) {
      setProductCategoryKeys(pcmKeys.filter((cat) => cat !== 'COVID'));
    } else {
      setProductCategoryKeys(pcmKeys);
    }
    // This needs to listen to productCategoryMap, too, but it's causing a memory leak
  }, [disableC19Param, flagToShowCovidCategory]);

  const allCategories = useMemo(
    () => [
      'All Products',
      ...productCategoryKeys.sort(
        (a: string, b: string) =>
          productCategoryMap[a].order - productCategoryMap[b].order,
      ),
    ],
    [productCategoryKeys],
  );
  const activeCategoryIdx = useRef<number>(0);
  const selectedCategory = useRef<string>('');
  const visiblePromos = useRef<PromoProps[]>([]);
  const categoryPromos = useRef<Record<string, Record<string, unknown>>>({
    'All Products': {},
  });
  // TODO: Revisit this after CG-1474
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [categoryPromo, setCategoryPromo] = useState<any>();
  const [filterResults, setFilterResults] = useState<ProductDataType[]>([]);

  const structuredProductData = structuredProductList(
    filterResults.map((product) => product.slug),
  );

  const trackCategoryFilter = (category: string): void => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: ANALYTICS.LABELS.PIP_CATEGORY_FILTER,
        category,
      },
    });
  };

  const handleCategorySelect = (i: number) => {
    /**
     * not best practice to set variables to undefined,
     * but this is how it was described in the docs here:
     * https://github.com/pbeshai/use-query-params#usequeryparam
     **/
    setActiveCategory(undefined);

    if (i > 0) {
      /**
       * Products can belong to multiple categories - we must remove duplicates from the filtered list. This is happening because we need to support category & categories currently.
       */
      const category = allCategories[i];
      const filteredTests = filterUniqueByCategory(showableTests, category);
      setFilterResults(filteredTests);
      trackCategoryFilter(category);
    } else {
      setFilterResults(uniqueTests);
    }
    activeCategoryIdx.current = i;
  };

  const directCategoryLink = (i: number) => {
    /**
     * This method is necessary because of the discrepancy between
     * the category number and the category order/index.
     *
     * also, it's not best practice to set variables to undefined,
     * but this is how it was described in the docs here:
     * https://github.com/pbeshai/use-query-params#usequeryparam
     **/
    if (i > 0) {
      const category = productCategoryKeys.find(
        (cat) => productCategoryMap[cat].pipId === i,
      );
      if (!category) return;
      /**
       * Products can belong to multiple categories - we must remove duplicates from the filtered list. This is happening because we need to support category & categories currently.
       */
      const filteredTests = filterUniqueByCategory(showableTests, category);
      setFilterResults(filteredTests);
      activeCategoryIdx.current = allCategories.indexOf(category);
    } else {
      setFilterResults(uniqueTests);
    }
  };

  const trackPageView = (): void => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.VIEWED_PAGE,
      data: {
        Page: 'PIP',
      },
    });
  };

  useEffect(() => {
    categoryPromos.current = pcmKeys.reduce(
      (acc: Record<string, Record<string, unknown>>, category) => {
        const { promoActive, promoRow, promoColumn, promoSpan, promo } =
          productCategoryMap[category];
        acc[category] = {
          active: promoActive,
          row: promoRow,
          column: promoColumn,
          span: promoSpan,
          ...promo,
        };
        return acc;
      },
      {},
    );

    if (activeCategory) {
      /**
       * Per https://everlyhealth.atlassian.net/browse/FE-520?focusedCommentId=188872
       * we need to set a hard redirect from category 4 to category 13.
       *
       * TODO: remove the need for this hard redirect by disabling category 4 in
       * all marketing campaigns
       */
      const activeCat = activeCategory === 4 ? 13 : activeCategory; // redirect to 13 if activeCategory is 4
      directCategoryLink(Number(activeCat));
    } else {
      setFilterResults(uniqueTests);
    }

    trackPageView();
  }, []);

  useEffect(() => {
    if (filterResults.length > 0) {
      selectedCategory.current = allCategories[activeCategoryIdx.current];
      setCategoryPromo(categoryPromos.current[selectedCategory.current]);
    }
  }, [filterResults]);

  useEffect(() => {
    if (activeCategoryIdx.current === 0) {
      visiblePromos.current = activePromos;
    } else if (categoryPromo) {
      visiblePromos.current = [categoryPromo];
    }
  }, [categoryPromo]);

  return (
    <div data-testid="pip-page">
      <SEO
        title={TITLES.PRODUCTS}
        description={DESCRIPTIONS.PRODUCTS}
        url={`${APP_ROOT}/products/`}
        canonicalUrl={'products'}
      />
      <StructuredData data={structuredProductData} />
      <Layout>
        <PageHeading />
        <PageControlSection
          handleCategorySelect={handleCategorySelect}
          allTabItems={allCategories}
          categoryIndex={activeCategoryIdx.current}
          numberOfResults={filterResults.length}
        />
        <PrePurchaseModalContextProvider>
          <ProductDisplayGrid
            products={filterResults}
            visiblePromos={visiblePromos.current}
            activePromo={activePromo}
          />
        </PrePurchaseModalContextProvider>
      </Layout>
    </div>
  );
};

export default PIPTemplate;
